/**
 * Phone input field with country code selector.
 *
 * Merges two nameless fields into one phone number field
 */
import {
  computed, Ref, ref, SetupContext,
  watch,
} from 'vue';
import {
  HekateBusFieldValue, HekateDisplayField, HekateDisplayForm, HekateEventBusCallbackParams,
} from 'www-shared';
import hekateBus from '../../../../bus';
import FormFieldCoordinator from './FormFieldCoordinator';
import FieldUtil from '../fields/FieldUtil';

type CountryCodeInfo = {
  code: string;
  twoChar: string;
}

/** Country codes in the format of `${LETTERS} ${NUMBERS},...` compact to save space */
const codeData = [
  'AF 93,AX 358,AL 355,DZ 213,AS 1-684,AD 376,AO 244,AI 1-264,AQ 672,AG 1-268,AR 54',
  ',AM 374,AW 297,AU 61,AT 43,AZ 994,BS 1-242,BH 973,BD 880,BB 1-246,BY 375,BE 32,B',
  'Z 501,BJ 229,BM 1-441,BT 975,BO 591,BQ 599,BA 387,BW 267,BV 47,BR 55,IO 246,VG 1',
  '-284,BN 673,BG 359,BF 226,BI 257,CV 238,KH 855,CM 237,CA 1,KY 1-345,CF 236,TD 23',
  '5,CL 56,CN 86,HK 852,MO 853,CX 61,CC 61,CO 57,KM 269,CG 242,CK 682,CR 506,HR 385',
  ',CU 53,CW 599,CY 357,CZ 420,KP 850,CD 243,DK 45,DK 45,DJ 253,DM 1-767,DO 1-809,D',
  'O 1-829,DO 1-849,EC 593,EG 20,SV 503,GQ 240,ER 291,EE 372,SZ 268,ET 251,FK 500,F',
  'O 298,FJ 679,FI 358,FR 33,GF 594,PF 689,TF 262,GA 241,GM 220,GE 995,DE 49,GH 233',
  ',GI 350,GR 30,GL 299,GD 1-473,GP 590,GU 1-671,GT 502,GG 44,GN 224,GW 245,GY 592,',
  'HT 509,HM 672,VA 39-06,HN 504,HU 36,IS 354,IN 91,ID 62,IR 98,IQ 964,IE 353,IM 44',
  ',IL 972,IT 39,CI 225,JM 1-876,JP 81,JE 44,JO 962,KZ 7,KE 254,KI 686,KW 965,KG 99',
  '6,LA 856,LV 371,LB 961,LS 266,LR 231,LY 218,LI 423,LT 370,LU 352,MG 261,MW 265,M',
  'Y 60,MV 960,ML 223,MT 356,MH 692,MQ 596,MR 222,MU 230,YT 262,MX 52,FM 691,MC 377',
  ',MN 976,ME 382,MS 1-664,MA 212,MZ 258,MM 95,NA 264,NR 674,NP 977,NL 31,NL 31,NC ',
  '687,NZ 64,NI 505,NE 227,NG 234,NU 683,NF 672,MP 1-670,MK 389,NO 47,OM 968,PK 92,',
  'PW 680,PA 507,PG 675,PY 595,PE 51,PH 63,PN 870,PL 48,PT 351,PR 1,QA 974,KR 82,MD',
  ' 373,RE 262,RO 40,RU 7,RW 250,BL 590,SH 290,KN 1-869,LC 1-758,MF 590,PM 508,VC 1',
  '-784,WS 685,SM 378,ST 239,SA 966,SN 221,RS 381,SC 248,SC 248,SL 232,SG 65,SX 1-7',
  '21,SK 421,SI 386,SB 677,SO 252,ZA 27,GS 500,SS 211,ES 34,LK 94,PS 970,SD 249,SR ',
  '597,SJ 47,SE 46,CH 41,SY 963,TW 886,TJ 992,TH 66,TL 670,TG 228,TK 690,TO 676,TT ',
  '1-868,TN 216,TR 90,TM 993,TC 1-649,TV 688,UG 256,UA 380,AE 971,GB 44,TZ 255,US 1',
  ',VI 1-340,UY 598,UZ 998,VU 678,VE 58,VN 84,WF 681,EH 212,EH 212,YE 967,ZM 260,ZW',
  ' 263'].join('');

type PhoneNumberMemory = {
  countryCode: string;
  phoneNumber: string;
}

export default class PhoneNumberFieldCoordinator extends FormFieldCoordinator {
  constructor({
    context,
    field,
    modelValue,
    formDefinition,
  }: {
    field: Ref<HekateDisplayField>;
    context: SetupContext;
    modelValue: Ref<string>;
    formDefinition: Ref<HekateDisplayForm>;
  }) {
    super({
      context,
      field,
      modelValue,
      formDefinition,
    });
  }

  private static getAllCountryCodeInfos(): Array<CountryCodeInfo> {
    const codePairs = codeData.split(',');
    const result: Array<CountryCodeInfo> = codePairs.map((curCodePair) => {
      const codePair = curCodePair.split(' ');
      if (codePair.length !== 2) {
        throw new Error('Invalid format for country code data');
      }
      return {
        code: codePair[1],
        twoChar: codePair[0],
      };
    });

    return result.sort((lhs, rhs) => lhs.twoChar.localeCompare(rhs.twoChar));
  }

  private static getPhoneNumberMemory(pn: string | number | boolean): PhoneNumberMemory {
    const emptyInfo: PhoneNumberMemory = {
      countryCode: '',
      phoneNumber: '',
    };
    if (typeof pn !== 'string' || !pn.startsWith('+')) {
      return emptyInfo;
    }
    const bits = pn.substring(1).split(' ');
    if (bits.length !== 2) {
      return emptyInfo;
    }
    const countryCode = bits[0];
    if (!PhoneNumberFieldCoordinator.getAllCountryCodeInfos().find((info) => info.code === countryCode)) {
      return emptyInfo;
    }
    return {
      countryCode,
      phoneNumber: bits.slice(1).join(' '),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  public setup() {
    const geoIpCountryCode = ref('');
    const countryCode = ref('');
    const phoneNumber = ref('');

    const yourCountryCodeInfo: Ref<CountryCodeInfo> = ref({
      code: '1',
      twoChar: 'US',
    });

    const allCountryCodeInfos = PhoneNumberFieldCoordinator.getAllCountryCodeInfos();

    // handle phone number that has been field injected
    if (this.field.value.val) {
      const pn = this.field.value.val.toString();
      if (!pn.startsWith('+')) {
        this.field.value.val = '';
      }
      const bits = pn.split(' ');
      const found = allCountryCodeInfos.find((info) => info.code === bits[0].substring(1));
      if (!found) {
        this.field.value.val = '';
      }
    }

    const commonCountryCodeInfos = ref<CountryCodeInfo[]>([
      { code: '1', twoChar: 'US' },
      { code: '44', twoChar: 'GB' },
      { code: '61', twoChar: 'AU' },
      { code: '49', twoChar: 'DE' },
      { code: '33', twoChar: 'FR' },
      { code: '91', twoChar: 'IN' },
      { code: '353', twoChar: 'IR' },
      { code: '971', twoChar: 'AE' },
      { code: '34', twoChar: 'ES' },
      { code: '51', twoChar: 'CO' },
    ]);

    // the value Eloqua will get
    const fullNumber = computed(() => {
      const cc = countryCode.value.trim();
      const pn = phoneNumber.value.trim();
      if (!cc || !pn) {
        return '';
      }
      return `+${cc} ${pn}`;
    });

    // bubble the value up to the form
    const bubbleModel = FieldUtil.bubbleModel({
      get: () => fullNumber.value,
      emitter: this.context,
    });

    // watch for changes in the full number, so they can be bubbled
    watch(fullNumber, (newVal) => {
      bubbleModel.value = newVal;
    });

    const phoneNumberMemory = PhoneNumberFieldCoordinator.getPhoneNumberMemory(this.field.value.val);
    countryCode.value = phoneNumberMemory.countryCode;
    phoneNumber.value = phoneNumberMemory.phoneNumber;

    watch(geoIpCountryCode, (newVal) => {
      const found = allCountryCodeInfos.find((info) => info.twoChar === newVal);
      if (!found) return;
      yourCountryCodeInfo.value = found;
    });

    // Watch for GEO-IP countryCode from injection so we can put the users country on top
    hekateBus.addEventListener('form-inject-value', (args: HekateEventBusCallbackParams<HekateBusFieldValue>) => {
      if (args.data.fieldName !== 'countryCode') return;
      geoIpCountryCode.value = `${args.data.fieldValue}`;
    });

    // request form injections immediately and with a delay, attempting to get users GEO-IP countryCode
    hekateBus.dispatchEvent('request-form-injections', {});
    setTimeout(() => {
      hekateBus.dispatchEvent('request-form-injections', {});
    }, 5000);

    // Conditionally emit events on the select field, if there are values in the
    // phone number field where the user inputs values
    const conditionallySelectEmit = (eventName: 'blur' | 'focus', field: HekateDisplayField) => {
      if (phoneNumber.value.length < 1) return;
      this.context.emit(eventName, field);
    };

    return {
      countryCode,
      phoneNumber,
      bubbleModel,
      allCountryCodeInfos,
      commonCountryCodeInfos,
      yourCountryCodeInfo,
      conditionallySelectEmit: conditionallySelectEmit.bind(this),
    };
  }
}
